'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { getBookingUrl } from '@/lib/booking';
import { useLocale } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const copy = getPublicStrings(locale).home.about;
  const aboutBookingHref = getBookingUrl('about_coach');
  const aboutBookingExternal = aboutBookingHref.startsWith('http');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-16 md:py-32 px-6 md:px-12 bg-white border-b border-border"
    >
      <div className="max-w-7xl mx-auto">
        {/* Eyebrow */}
        <div
          className={`mb-8 transition-all duration-800 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <span className="text-[10px] tracking-[0.3em] uppercase text-gold font-black">
            {copy.eyebrow}
          </span>
          <div className="w-12 h-[2px] bg-gold mt-2" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left - Image */}
          <div
            className={`lg:col-span-5 transition-all duration-800 delay-100 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            <div className="relative group">
              <div className="relative aspect-[3/4] overflow-hidden img-mask">
                <AppImage
                  src="/assets/images/aos-about-coach.jpg"
                  alt={copy.imageAlt}
                  fill
                  sizes="(max-width: 1024px) 100vw, 40vw"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              {/* Floating Stat Card */}
              <div className="absolute -bottom-6 -right-6 bg-white p-8 shadow-xl border-l-4 border-gold">
                <div className="text-4xl font-heading font-extrabold text-primary mb-2">
                  15<span className="text-xl text-gold">+</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-foreground font-bold">
                  {copy.yearsExperience}
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div
            className={`lg:col-span-7 transition-all duration-800 delay-200 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            <h2 className="text-4xl md:text-6xl text-foreground leading-tight font-heading font-bold mb-8">
              {copy.title} <span className="text-gold italic">{copy.titleItalic}</span>
            </h2>

            <div className="space-y-6 border-l-2 border-gold/30 pl-8">
              <p className="text-lg text-foreground/80 font-light leading-relaxed">{copy.p1}</p>
              <p className="text-lg text-foreground/80 font-light leading-relaxed">{copy.p2}</p>
              <p className="text-lg text-foreground/80 font-light leading-relaxed">{copy.p3}</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-8 mt-12 pt-8 border-t border-border">
              <div>
                <div className="text-3xl font-heading font-bold text-primary mb-2">
                  500<span className="text-xl text-gold">+</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                  {copy.playersCoached}
                </div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-primary mb-2">
                  4<span className="text-xl text-gold">+</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                  {copy.continentsPlayed}
                </div>
              </div>
            </div>

            <a
              href={aboutBookingHref}
              {...(aboutBookingExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="mt-10 inline-flex min-h-[48px] items-center gap-2 bg-primary px-8 py-4 text-xs font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-primary/90"
            >
              {copy.cta}
              <Icon name="ArrowRightIcon" size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
